import type { IdZonaOperacaoTampa } from '../../dominio/didatico/oficinaPortaMalas'
import { Y_TAMPA_TRASEIRA_BAIXA, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import type { AjusteFerramentaPorta } from './zonasOperacaoPorta'

export interface ZonaOperacaoTampa3D {
  id: IdZonaOperacaoTampa
  nome: string
  posicao: [number, number, number]
  /** Inclinação recomendada da ferramenta no plano da tampa. */
  anguloGraus: number
}

export interface AvaliacaoContatoTampa {
  zona: IdZonaOperacaoTampa | null
  distancia: number | null
  contatoValido: boolean
  orientacaoValida: boolean
}

export const TOLERANCIA_CONTATO_TAMPA = 0.2
export const TOLERANCIA_ANGULO_TAMPA_GRAUS = 30

/** Quinas do plano da tampa, as mesmas usadas por `TampaTraseira` em `Carroceria.tsx`. */
function quinasDaTampa(perfil: PerfilVeiculo) {
  const hatch = perfil.volumeTraseiro === 'hatch'
  return {
    yTopo: hatch ? perfil.cinturaY - 0.04 : perfil.cinturaY,
    zTopo: perfil.colunaC.zBase - 0.02,
    yBaixo: hatch ? Y_TAMPA_TRASEIRA_BAIXA : perfil.cinturaY + 0.02,
    zBaixo: perfil.traseiraZ + 0.04,
  }
}

/**
 * Inclinação do plano da tampa em torno do eixo X.
 *
 * O vidro do hatch desce da linha de cintura até o para-choque, e a tampa do
 * sedã é quase horizontal: um marcador desenhado sempre no mesmo plano ficaria
 * atravessado na chapa em uma das duas carrocerias.
 */
export function inclinacaoDaTampaRad(perfil: PerfilVeiculo): number {
  const { yTopo, zTopo, yBaixo, zBaixo } = quinasDaTampa(perfil)
  return Math.atan2(zBaixo - zTopo, yBaixo - yTopo)
}

/**
 * Pontos didáticos derivados das mesmas coordenadas usadas por `TampaTraseira`
 * em `Carroceria.tsx` — a quina de cima (dobradiças) e a fresta de baixo
 * (trava), no hatch e no sedan.
 */
export function zonasOperacaoTampa(perfil: PerfilVeiculo): readonly ZonaOperacaoTampa3D[] {
  const { yTopo, zTopo, yBaixo, zBaixo } = quinasDaTampa(perfil)
  const xDobradica = perfil.largura / 2 - 0.15
  const xApoio = perfil.largura / 2 - 0.22
  const meioY = (yTopo + yBaixo) / 2
  const meioZ = (zTopo + zBaixo) / 2

  return [
    { id: 'chapa', nome: 'Chapa da tampa', posicao: [0, meioY, meioZ], anguloGraus: 90 },
    { id: 'trava', nome: 'Pino da trava', posicao: [0, yBaixo + 0.04, zBaixo - 0.02], anguloGraus: 90 },
    { id: 'dobradicaEsquerda', nome: 'Dobradiça esquerda', posicao: [-xDobradica, yTopo - 0.02, zTopo], anguloGraus: 0 },
    { id: 'dobradicaDireita', nome: 'Dobradiça direita', posicao: [xDobradica, yTopo - 0.02, zTopo], anguloGraus: 0 },
    { id: 'apoio', nome: 'Apoio a gás', posicao: [xApoio, meioY, meioZ + 0.05], anguloGraus: 45 },
  ]
}

export function zonaOperacaoTampaPorId(
  perfil: PerfilVeiculo,
  idZona: IdZonaOperacaoTampa | null,
): ZonaOperacaoTampa3D | null {
  if (!idZona) return null
  return zonasOperacaoTampa(perfil).find((zona) => zona.id === idZona) ?? null
}

export function posicaoFerramentaNaTampa(
  perfil: PerfilVeiculo,
  idZona: IdZonaOperacaoTampa | null,
  ajuste: AjusteFerramentaPorta,
): [number, number, number] | null {
  const zona = zonaOperacaoTampaPorId(perfil, idZona)
  if (!zona) return null
  return [zona.posicao[0] + ajuste.deslocamentoY, zona.posicao[1], zona.posicao[2] + ajuste.deslocamentoZ]
}

/** Avalia contato e orientação sem depender do marcador originalmente escolhido. */
export function avaliarContatoFerramentaNaTampa(
  perfil: PerfilVeiculo,
  posicao: [number, number, number] | null,
  anguloGraus: number,
): AvaliacaoContatoTampa {
  if (!posicao) return { zona: null, distancia: null, contatoValido: false, orientacaoValida: false }

  const zonas = zonasOperacaoTampa(perfil)
  const ordenadas = zonas
    .map((zona) => ({
      zona,
      distancia: Math.hypot(posicao[0] - zona.posicao[0], posicao[2] - zona.posicao[2]),
    }))
    .sort((a, b) => a.distancia - b.distancia)
  const proxima = ordenadas[0]
  if (!proxima || proxima.distancia > TOLERANCIA_CONTATO_TAMPA) {
    return { zona: null, distancia: proxima?.distancia ?? null, contatoValido: false, orientacaoValida: false }
  }

  const diferencaBruta = Math.abs(anguloGraus - proxima.zona.anguloGraus) % 180
  const diferenca = Math.min(diferencaBruta, 180 - diferencaBruta)
  return {
    zona: proxima.zona.id,
    distancia: proxima.distancia,
    contatoValido: true,
    orientacaoValida: diferenca <= TOLERANCIA_ANGULO_TAMPA_GRAUS,
  }
}
